"use client";

import { Building2, Factory, ShoppingBag, type LucideIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CcProductCanvas } from "@/components/home/product-canvas/CcProductCanvas";
import type { CcProductCanvasFlowCopy } from "@/components/home/product-canvas/CcProductCanvas";
import { CcProductCanvasFrameIntegrations } from "@/components/home/product-canvas/CcProductCanvasFrameIntegrations";
import type {
  AiAutomationHubspotSubpageProductCanvasCopy,
  AiAutomationHubspotSubpageProductCanvasFlow,
} from "@/components/services/ai-automation/ai-automation-page-copy";
import { CcFaqPillTabs } from "@/components/ui/CcFaqPillTabs";
import type { SeoWorkflowConnectIntegrationCopy } from "@/components/home/seo-workflow/seo-workflow-copy";

const INTEGRATIONS_CROSSFADE_MS = 220;

const HUBSPOT_INDUSTRY_TAB_ICONS: Record<string, LucideIcon> = {
  manufacturing: Factory,
  "b2b-saas": Building2,
  ecommerce: ShoppingBag,
};

function HubSpotIndustryTabLabel({ tabId, label }: { tabId: string; label: string }) {
  const Icon = HUBSPOT_INDUSTRY_TAB_ICONS[tabId];
  if (!Icon) {
    return label;
  }

  return (
    <>
      <Icon className="cc-hubspot-product-canvas__tab-icon" aria-hidden="true" size={14} strokeWidth={2} />
      <span className="cc-hubspot-product-canvas__tab-label">{label}</span>
    </>
  );
}

type CcHubSpotAutomationProductCanvasProps = {
  copy: AiAutomationHubspotSubpageProductCanvasCopy;
  /** Hero right column: canvas only (heading lives in delivery copy column). */
  placement?: "hero" | "section";
};

function toCanvasFlow(
  flow: AiAutomationHubspotSubpageProductCanvasFlow,
): CcProductCanvasFlowCopy {
  const { integrations: _integrations, ...canvasFlow } = flow;
  return canvasFlow;
}

function integrationsSignature(integrations: SeoWorkflowConnectIntegrationCopy[]): string {
  return integrations.map((item) => item.id).join("|");
}

/** Light embedded CcProductCanvas for the HubSpot subpage. */
export function CcHubSpotAutomationProductCanvas({
  copy,
  placement = "section",
}: CcHubSpotAutomationProductCanvasProps) {
  const defaultTabId = copy.industryTabs[0]?.id ?? "";
  const [activeTabId, setActiveTabId] = useState(defaultTabId);

  const activeTab =
    copy.industryTabs.find((tab) => tab.id === activeTabId) ?? copy.industryTabs[0];

  const activeFlowRecord = useMemo(() => {
    if (!activeTab) return undefined;
    return copy.flows[activeTab.flowId];
  }, [activeTab, copy.flows]);

  const activeFlow = useMemo(() => {
    if (!activeFlowRecord) return undefined;
    return toCanvasFlow(activeFlowRecord);
  }, [activeFlowRecord]);

  const activeIntegrations = activeFlowRecord?.integrations ?? [];

  const [presentedIntegrations, setPresentedIntegrations] =
    useState(activeIntegrations);
  const [isIntegrationsCrossfadeOut, setIsIntegrationsCrossfadeOut] = useState(false);
  const integrationsSignatureRef = useRef(integrationsSignature(activeIntegrations));
  const integrationsCrossfadeTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const nextSignature = integrationsSignature(activeIntegrations);

    if (nextSignature === integrationsSignatureRef.current) {
      return;
    }

    integrationsSignatureRef.current = nextSignature;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      setIsIntegrationsCrossfadeOut(false);
      setPresentedIntegrations(activeIntegrations);
      return;
    }

    window.clearTimeout(integrationsCrossfadeTimerRef.current);
    setIsIntegrationsCrossfadeOut(true);

    integrationsCrossfadeTimerRef.current = window.setTimeout(() => {
      setPresentedIntegrations(activeIntegrations);
      setIsIntegrationsCrossfadeOut(false);
    }, INTEGRATIONS_CROSSFADE_MS);

    return () => {
      window.clearTimeout(integrationsCrossfadeTimerRef.current);
    };
  }, [activeIntegrations]);

  const onSelectTab = useCallback((id: string) => {
    setActiveTabId(id);
  }, []);

  const frameIntegrations =
    placement === "hero" && presentedIntegrations.length > 0 ? (
      <CcProductCanvasFrameIntegrations
        linkingLabel={copy.integrationsLinking}
        integrations={presentedIntegrations}
        isCrossfadeOut={isIntegrationsCrossfadeOut}
      />
    ) : undefined;

  const industryTabs =
    placement === "hero" && copy.industryTabs.length > 0 ? (
      <div className="cc-hubspot-product-canvas__tabs-shell cc-faq-light-tabs">
        <CcFaqPillTabs
          className="cc-hubspot-product-canvas__tabs"
          items={copy.industryTabs.map((tab) => ({
            id: tab.id,
            label: <HubSpotIndustryTabLabel tabId={tab.id} label={tab.label} />,
            tabId: `cc-hubspot-product-canvas-tab-${tab.id}`,
          }))}
          activeId={activeTab?.id ?? defaultTabId}
          onSelect={onSelectTab}
          ariaLabel={copy.tablistLabel}
        />
      </div>
    ) : undefined;

  const canvas = (
    <CcProductCanvas
      eyebrow={copy.eyebrow}
      title={copy.title}
      body={copy.body}
      flow={activeFlow}
      theme="light"
      layout="embedded"
      showTiles={false}
      showHeading={placement !== "hero"}
      showStack={placement !== "hero"}
      showFrameOrnaments={placement !== "hero"}
      showChrome={placement !== "hero"}
      frameHeader={industryTabs}
      frameFooter={frameIntegrations}
      flowOverflow={placement === "hero" ? "horizontal-scroll" : undefined}
      id="hubspot-product-canvas"
      devSectionKey="hubspot-product-canvas"
      navTheme="light"
    />
  );

  if (placement === "hero") {
    const tabPanelId = activeTab
      ? `cc-hubspot-product-canvas-panel-${activeTab.id}`
      : undefined;

    return (
      <div className="cc-hubspot-product-canvas cc-hubspot-product-canvas--hero">
        <div
          id={tabPanelId}
          className="cc-hubspot-product-canvas__panel"
          role="tabpanel"
          aria-labelledby={
            activeTab ? `cc-hubspot-product-canvas-tab-${activeTab.id}` : undefined
          }
        >
          {canvas}
        </div>
      </div>
    );
  }

  return <div className="cc-hubspot-product-canvas">{canvas}</div>;
}
